import * as React from "react";

import { ApiError, listAgentRequests } from "@/api/client";
import { AgentRequestView } from "@/api/types";

import { RequestCard } from "./RequestCard";

/**
 * Who asked this host for time, and what came of it.
 *
 * <p>Its own page rather than a drawer (D-68). A drawer is for glancing at something and closing
 * it; this is where a host decides whether a stranger gets an hour of their week, reads what that
 * stranger actually said, and later goes back to find out who they let in. That is a place, and it
 * needs a URL.</p>
 *
 * <p>The one rule the layout enforces: <b>what a provider vouched for never looks like what the
 * caller typed</b> (D-6). A name and an address read identically on a screen and mean completely
 * different things, and the whole admission design turns on the difference. So a verified address
 * carries a mark and a plain one carries the word "unverified" — never the mark by default with an
 * absence to notice.</p>
 */
export function AgentRequestsPage() {
  const [rows, setRows] = React.useState<AgentRequestView[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    try {
      setRows(await listAgentRequests());
    } catch (e) {
      setError(describe(e));
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  async function act(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await action();
      await reload();
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(null);
    }
  }

  const waiting = (rows ?? []).filter((r) => r.decision?.state === "AWAITING_YOU");
  // Its own section, above the archive and below the timed decisions. Not mixed into "Waiting on
  // you": those hold an hour and run out, these hold nothing and nobody is waiting (D-94).
  const consider = (rows ?? []).filter((r) => r.decision?.state === "CONSIDER");
  const rest = (rows ?? []).filter(
    (r) => r.decision?.state !== "AWAITING_YOU" && r.decision?.state !== "CONSIDER",
  );

  return (
    // No nav and no background wrapper: AppShell is the layout route for every signed-in page and
    // owns both. Rendering a second AppNav here put two of them in the document, and because the
    // nav is `sticky top-0` they only overlapped at the very top of the scroll — which is why it
    // read as an intermittent glitch rather than a duplicated element.
    <main className="mx-auto max-w-3xl px-6 py-6">
      <h1 className="font-comic-headline text-lg uppercase tracking-wide">Agent requests</h1>

      {error && (
        <p role="alert" className="mt-3 rounded border-2 border-red-700 bg-red-50 px-3 py-2 text-xs text-red-800">
          {error}
        </p>
      )}

      {rows === null ? (
        <p className="mt-4 text-xs text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Nobody has asked yet.</p>
      ) : (
        <>
          {waiting.length > 0 && (
            <section className="mt-4">
              <h2 className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground">
                Waiting on you
              </h2>
              <ul className="mt-2 space-y-2">
                {waiting.map((row) => (
                  <li key={row.id}>
                    <RequestCard row={row} busy={busy} onAct={act} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {consider.length > 0 && (
            <section className="mt-6">
              <h2 className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground">
                Nobody could take this
              </h2>
              <ul className="mt-2 space-y-2">
                {consider.map((row) => (
                  <li key={row.id}>
                    <RequestCard row={row} busy={busy} onAct={act} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-6">
            <h2 className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground">
              Everything else
            </h2>
            <ul className="mt-2 space-y-2">
              {rest.map((row) => (
                <li key={row.id}>
                  <RequestCard row={row} busy={busy} onAct={act} />
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </main>
  );
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 409) {
      try {
        const detail = JSON.parse(e.body || "{}").detail;
        // The host's own diary, so this is safe to be specific about: the hour went while they
        // were deciding, or the calendar would not take it.
        if (typeof detail === "string") return detail;
      } catch {
        /* fall through */
      }
      return "That hour is no longer free.";
    }
    if (e.status === 404) return "That request is no longer there.";
  }
  return "Something went wrong. Please try again.";
}
